import {
  useState,
  useEffect,
  useMemo,
  useRef
} from 'react'
import {
  useParams,
  useSearchParams
} from 'react-router-dom'
import {
  startOfMonth
} from 'date-fns'
import {
  CheckCircle2
} from 'lucide-react'
import {
  trackEvent,
  SchedulingEvents,
  buildPostHogHashFragment
} from '../analytics/posthog'
import {
  bookPublicSlotApiV1SchedulingPublicLinkIdBookPost,
  castPublicPollVotesApiV1SchedulingPublicLinkIdVotesPost,
  contactLinkOwnerApiV1SchedulingPublicLinkIdContactPost,
  getPublicLinkApiV1SchedulingPublicLinkIdGet,
  getPublicLinkForInviteeApiV1SchedulingPublicLinkIdITokenGet,
  getPublicLinkSlotsApiV1SchedulingPublicLinkIdSlotsGet,
  verifyPublicEmailApiV1SchedulingPublicLinkIdVerifyEmailPost
} from '../client'
import type {
  BookSlotRequest,
  BookingConfirmationResponse,
  CastPollVotesRequest,
  GuestFieldConfig,
  PublicSchedulingLinkResponse,
  SchedulingContactResponse,
  SchedulingSlotResponse
} from '../client'
import {
  API_BASE_URL
} from '../config'
import {
  EventInfoRail
} from './scheduling/EventInfoRail'
import {
  MonthCalendar
} from './scheduling/MonthCalendar'
import {
  DayTimeList
} from './scheduling/DayTimeList'
import {
  TimezoneSelector
} from './scheduling/TimezoneSelector'
import {
  GUEST_TIMEZONE,
  dateKeyToLocalDate,
  formatDayLabelFromKey,
  formatTime,
  groupSlotsByDateKey,
  isValidTimezone,
  toDateKey
} from './scheduling/schedulingUtils'
import {
  useVisitorAvailability
} from './scheduling/useVisitorAvailability'
import {
  VisitorAvailabilityControls
} from './scheduling/VisitorAvailabilityControls'

const CATCH_HOME_URL_WITH_UTM = 'https://catchagent.ai?'
  + new URLSearchParams({
    utm_source: 'catch_booking_link',
    utm_medium: 'powered_by',
  }).toString()

function buildCatchHomeHref(): string {
  return CATCH_HOME_URL_WITH_UTM + buildPostHogHashFragment()
}

// FastAPI puts a human-readable string in `detail` for expected failures
// (link revoked, host calendar disconnected); validation errors put a list
// there. Only surface the string form.
function errorDetail(error: unknown): string | null {
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail
    if (typeof detail === 'string') {
      return detail
    }
  }
  return null
}

const INPUT_CLASS = 'w-full border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500'

// Card chrome shared by the time-selection, confirmation and poll views: a
// host-info rail beside the flow-specific content, stacking on mobile.
function BookingShell({
  link,
  railChildren,
  children
}: {
  link: PublicSchedulingLinkResponse,
  railChildren?: React.ReactNode,
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="md:flex md:divide-x md:divide-gray-200">
        <EventInfoRail
          link={link}
          className="md:w-56 md:shrink-0 border-b border-gray-200 md:border-b-0"
        >
          {railChildren}
        </EventInfoRail>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  )
}

function SlotsAreaSkeleton(): JSX.Element {
  return (
    <div className="md:flex md:divide-x md:divide-gray-200 animate-pulse" aria-hidden="true">
      <div className="flex-1 min-w-0 p-6">
        <div className="h-4 w-40 bg-gray-200 rounded mb-5" />
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="flex justify-center">
              <div className="w-10 h-10 rounded-full bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
      <div className="md:w-64 md:shrink-0 p-6 space-y-2">
        <div className="h-4 w-32 bg-gray-200 rounded mb-4" />
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-11 bg-gray-100 border border-gray-200 rounded-lg" />
        ))}
      </div>
    </div>
  )
}

function ContactAssistantSection({
  link,
  linkId
}: {
  link: PublicSchedulingLinkResponse,
  linkId: string
}): JSX.Element {
  const [showForm, setShowForm] = useState(false)
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactMessage, setContactMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [contactResult, setContactResult] = useState<SchedulingContactResponse | null>(null)

  if (!link.assistant_name) return <></>

  const handleSubmit = async () => {
    if (!contactName || !contactEmail || !contactMessage) return
    setSubmitting(true)
    try {
      const { data, error } = await contactLinkOwnerApiV1SchedulingPublicLinkIdContactPost({
        path: { link_id: linkId },
        body: {
          name: contactName,
          email: contactEmail,
          message: contactMessage,
        },
        baseURL: API_BASE_URL,
        throwOnError: false,
      })
      if (error || !data) {
        throw new Error('Failed to send request')
      }
      setContactResult(data)
      trackEvent(SchedulingEvents.CONTACT_ASSISTANT_SUBMITTED, {
        link_id: linkId,
      })
    } catch {
      setContactResult({
        success: false,
        message: 'Something went wrong. Please try again.'
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (contactResult) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
        <p className="text-gray-600">{contactResult.message}</p>
      </div>
    )
  }

  if (!showForm) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3">
          {link.assistant_picture_url && (
            <img
              src={link.assistant_picture_url}
              alt={link.assistant_name}
              className="w-10 h-10 rounded-full object-cover"
            />
          )}
          <div className="flex-1">
            <p className="text-sm text-gray-600">
              Can&apos;t find a time that works?
            </p>
            <button
              onClick={() => {
                trackEvent(SchedulingEvents.CONTACT_ASSISTANT_OPENED, {
                  link_id: linkId,
                })
                setShowForm(true)
              }}
              className="text-sm text-primary-500 hover:text-primary-600 font-medium"
            >
              Ask {link.assistant_name}, {link.owner_name}&apos;s assistant, to reach out
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        {link.assistant_picture_url && (
          <img
            src={link.assistant_picture_url}
            alt={link.assistant_name}
            className="w-10 h-10 rounded-full object-cover"
          />
        )}
        <p className="text-sm font-medium text-gray-900">
          {link.assistant_name} is {link.owner_name}&apos;s executive assistant
        </p>
      </div>
      <div className="space-y-3">
        <input
          type="text"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          className={INPUT_CLASS}
          placeholder="Your name"
        />
        <input
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          className={INPUT_CLASS}
          placeholder="Your email"
        />
        <textarea
          value={contactMessage}
          onChange={(e) => setContactMessage(e.target.value)}
          className={INPUT_CLASS}
          rows={3}
          placeholder={`Let ${link.assistant_name} know what you need`}
        />
      </div>
      <div className="flex gap-3 mt-4">
        <button
          onClick={() => setShowForm(false)}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!contactName || !contactEmail || !contactMessage || submitting}
          className="px-4 py-2 text-sm text-white bg-primary-500 rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  )
}

function GuestFieldInput({
  field,
  value,
  onChange
}: {
  field: GuestFieldConfig,
  value: string,
  onChange: (value: string) => void
}): JSX.Element {
  const label = field.label + (field.required ? '' : ' (optional)')
  const fieldType = field.field_type ?? 'text'

  if (fieldType === 'textarea') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={INPUT_CLASS}
          rows={3}
        />
      </div>
    )
  }

  const inputType = fieldType === 'phone' ? 'tel' : fieldType

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={INPUT_CLASS}
      />
    </div>
  )
}

export function SchedulingBookingPage(): JSX.Element {
  const { linkId, token } = useParams<{ linkId: string, token?: string }>()
  const [searchParams] = useSearchParams()
  const tzParam = searchParams.get('tz')
  const guestTimezone = useMemo(
    () => (tzParam && isValidTimezone(tzParam)) ? tzParam : GUEST_TIMEZONE,
    [tzParam]
  )

  const [link, setLink] = useState<PublicSchedulingLinkResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // null = still loading; an array (possibly empty) once the slots endpoint returns.
  const [availableSlots, setAvailableSlots] = useState<SchedulingSlotResponse[] | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<SchedulingSlotResponse | null>(null)
  // Calendar state for the Calendly-style day picker.
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null)
  const [monthCursor, setMonthCursor] = useState<Date>(() => startOfMonth(new Date()))
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  // Advisory "did you mean ...?" / "looks off" hint from the verify-email
  // endpoint. Purely informational — it never blocks booking.
  const [emailHint, setEmailHint] = useState<{ suggestion: string | null, invalid: boolean } | null>(null)
  // True while the verify-email request is in flight, so the form can show a
  // "Checking…" state instead of a stale/empty hint.
  const [emailChecking, setEmailChecking] = useState(false)
  const [notes, setNotes] = useState('')
  const [customFields, setCustomFields] = useState<Record<string, string>>({})
  const [additionalAttendees, setAdditionalAttendees] = useState<string[]>([])
  const [newAttendee, setNewAttendee] = useState('')
  const [booking, setBooking] = useState(false)
  const [confirmation, setConfirmation] = useState<BookingConfirmationResponse | null>(null)
  const [displayTimezone, setDisplayTimezone] = useState(guestTimezone)
  // POLL-mode state — independent of the SOLO/COLLECTIVE/etc. booking flow.
  const [pollSelectedSlotIds, setPollSelectedSlotIds] = useState<Set<number>>(new Set())
  const [pollSubmitting, setPollSubmitting] = useState(false)
  const [pollSubmitted, setPollSubmitted] = useState(false)
  // Skip re-verifying (and re-charging) the same address on repeated blurs.
  const lastVerifiedEmailRef = useRef<string | null>(null)
  // Visitor-availability overlay ("show times you're free"), flag-gated via
  // link.visitor_availability. The hook owns the whole pipeline — OAuth,
  // free/busy map, analytics, the lead POST and the hide-busy filter; the
  // page just renders its state.
  const {
    state: visitorAvailability,
    hideBusy: hideBusySlots,
    setHideBusy: setHideBusySlots,
    connect: connectVisitorCalendar,
    disconnect: disconnectVisitorCalendar,
  } = useVisitorAvailability({ linkId, link, slots: availableSlots })
  const slotAvailabilityMap = visitorAvailability.availability
  // The overlay props stitched onto the booking-funnel events — one
  // derivation for all three event sites.
  const visitorSlotProps = (slotId: number) => ({
    visitor_availability_used: visitorAvailability.status === 'connected',
    visitor_slot_status: slotAvailabilityMap?.get(slotId) ?? null,
  })

  // The per-invitee token sits in the URL path. Without a strict referrer
  // policy, any third-party subresource (fonts, analytics, embed iframes, a
  // future Stripe widget) would leak the full URL — and therefore the token
  // — via the `Referer` header. The token is a capability grant: anyone with
  // a referer log could book or vote as that invitee. We pin the policy to
  // strict-origin while the booking page is mounted and clean it up on
  // unmount so the rest of the SPA keeps its default.
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'referrer'
    meta.content = 'strict-origin'
    document.head.appendChild(meta)
    return () => {
      document.head.removeChild(meta)
    }
  }, [])

  useEffect(() => {
    if (!linkId) return

    // Capture UTM params (set on book-a-demo links from emails) so PostHog
    // can attribute bookings back to the source email/CTA.
    const utmSource = searchParams.get('utm_source')
    const utmMedium = searchParams.get('utm_medium')
    const utmCampaign = searchParams.get('utm_campaign')
    const utmContent = searchParams.get('utm_content')

    // Fetch metadata first so the page layout can render immediately. When a
    // per-invitee token is on the URL, hit the bound endpoint so the response
    // includes the invitee's name/email for prefill.
    const metadataPromise = token
      ? getPublicLinkForInviteeApiV1SchedulingPublicLinkIdITokenGet({
        path: { link_id: linkId, token },
        baseURL: API_BASE_URL,
        throwOnError: false,
      })
      : getPublicLinkApiV1SchedulingPublicLinkIdGet({
        path: { link_id: linkId },
        baseURL: API_BASE_URL,
        throwOnError: false,
      })
    metadataPromise
      .then(({ data, error: linkError }) => {
        setLoading(false)
        if (linkError || !data) {
          // Surface the backend's `detail` so booker-facing messages
          // (e.g. "host's calendar isn't connected") aren't flattened to
          // a generic string. Fall back if the body is missing/malformed.
          setError(errorDetail(linkError) ?? 'This scheduling link is not available.')
          return
        }
        setLink(data)
        if (data.invitee) {
          setName(data.invitee.name)
          setEmail(data.invitee.email)
        }
        trackEvent(SchedulingEvents.LINK_VIEWED, {
          link_id: linkId,
          duration_minutes: data.duration_minutes,
          owner_name: data.owner_name,
          assistant_name: data.assistant_name,
          has_assistant: data.assistant_name !== null,
          guest_field_count: (data.guest_fields ?? []).length,
          invitee_bound: data.invitee_access_mode === 'invitee_bound',
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
          utm_content: utmContent,
        })
      })

    // Slots are fetched in parallel — they involve calendar API calls and may
    // be slow. While in flight, the slot grid renders as a skeleton.
    getPublicLinkSlotsApiV1SchedulingPublicLinkIdSlotsGet({
      path: { link_id: linkId },
      baseURL: API_BASE_URL,
    })
      .then(
        ({ data }) => setAvailableSlots(data.available_slots),
        // Fall back to "No available times" rather than a perpetual skeleton.
        // The contact-assistant form remains usable in the error state.
        () => setAvailableSlots([]),
      )
  }, [linkId])

  // "Hide times I'm busy" filters at the source, before grouping, so the
  // month calendar and day auto-selection agree with the time list — a day
  // whose every slot is busy dims out instead of reading "No times available".
  const visitorFilteredSlots = useMemo(
    () => (
      availableSlots && hideBusySlots && slotAvailabilityMap
        ? availableSlots.filter((slot) => slotAvailabilityMap.get(slot.id) !== 'busy')
        : availableSlots
    ),
    [availableSlots, hideBusySlots, slotAvailabilityMap]
  )
  const slotsByDateKey = useMemo(
    () => (visitorFilteredSlots ? groupSlotsByDateKey(visitorFilteredSlots, displayTimezone) : {}),
    [visitorFilteredSlots, displayTimezone]
  )
  const sortedDateKeys = useMemo(() => Object.keys(slotsByDateKey).sort(), [slotsByDateKey])
  const availableDateKeys = useMemo(() => new Set(sortedDateKeys), [sortedDateKeys])
  const { minMonth, maxMonth } = useMemo(() => {
    const firstKey = sortedDateKeys[0]
    const lastKey = sortedDateKeys[sortedDateKeys.length - 1]
    if (!firstKey || !lastKey) {
      const now = startOfMonth(new Date())
      return { minMonth: now, maxMonth: now }
    }
    return {
      minMonth: startOfMonth(dateKeyToLocalDate(firstKey)),
      maxMonth: startOfMonth(dateKeyToLocalDate(lastKey)),
    }
  }, [sortedDateKeys])

  // Auto-select the first available day once slots arrive, and re-pick it if a
  // timezone switch re-buckets the slots so the current selection loses its
  // times. A still-valid selection is left untouched.
  useEffect(() => {
    if (!availableSlots) return
    if (sortedDateKeys.length === 0) {
      setSelectedDateKey(null)
      return
    }
    if (selectedDateKey && slotsByDateKey[selectedDateKey]) return
    const first = sortedDateKeys[0]
    if (!first) return
    setSelectedDateKey(first)
    setMonthCursor(startOfMonth(dateKeyToLocalDate(first)))
  }, [availableSlots, sortedDateKeys, slotsByDateKey, selectedDateKey])

  const handleAddAttendee = () => {
    const trimmed = newAttendee.trim()
    if (trimmed && !additionalAttendees.includes(trimmed)) {
      setAdditionalAttendees([...additionalAttendees, trimmed])
      setNewAttendee('')
    }
  }

  const handleRemoveAttendee = (emailToRemove: string) => {
    setAdditionalAttendees(additionalAttendees.filter((e) => e !== emailToRemove))
  }

  // Fire-and-forget email check on blur. Surfaces a non-blocking typo hint;
  // any failure (or an uncertain/unconfigured verdict) just stays silent so a
  // verification hiccup can never get between a guest and their booking.
  const handleVerifyEmail = async () => {
    const candidate = email.trim()
    // Don't verify a host-locked (invitee-bound) address or incomplete input,
    // and don't re-spend a credit on a value we already checked.
    if (!linkId || !candidate || !candidate.includes('@') || link?.invitee) return
    if (lastVerifiedEmailRef.current === candidate) return
    lastVerifiedEmailRef.current = candidate
    setEmailChecking(true)
    try {
      const { data, error: verifyError } = await verifyPublicEmailApiV1SchedulingPublicLinkIdVerifyEmailPost({
        path: { link_id: linkId },
        body: { email: candidate },
        baseURL: API_BASE_URL,
        throwOnError: false,
      })
      if (verifyError || !data) {
        setEmailHint(null)
        return
      }
      const suggestion = data.suggested_email && data.suggested_email !== candidate
        ? data.suggested_email
        : null
      // Only speak up when we're confident: a concrete correction, or a
      // definitively-checked "invalid". Unknown/degraded verdicts stay quiet.
      if (suggestion || (data.checked && data.status === 'invalid')) {
        setEmailHint({ suggestion, invalid: data.status === 'invalid' })
      } else {
        setEmailHint(null)
      }
    } catch {
      setEmailHint(null)
    } finally {
      setEmailChecking(false)
    }
  }

  const isFormValid = (): boolean => {
    if (!name || !email) return false
    if (!link) return false
    if (newAttendee.trim()) return false
    for (const field of link.guest_fields ?? []) {
      if (field.required && !customFields[field.name]) return false
    }
    return true
  }

  const handleBook = async () => {
    if (!linkId || !selectedSlot || !isFormValid()) return

    setBooking(true)
    trackEvent(SchedulingEvents.BOOKING_SUBMITTED, {
      link_id: linkId,
      slot_id: selectedSlot.id,
      additional_attendee_count: additionalAttendees.length,
      ...visitorSlotProps(selectedSlot.id),
    })
    try {
      const body: BookSlotRequest = {
        slot_id: selectedSlot.id,
        invitee_name: name,
        invitee_email: email,
        invitee_timezone: guestTimezone,
        notes: notes || null,
      }
      if (Object.keys(customFields).length > 0) {
        body.custom_fields = customFields
      }
      if (additionalAttendees.length > 0) {
        body.additional_attendees = additionalAttendees
      }
      if (token) {
        body.invitee_token = token
      }

      const { data, error: bookError } = await bookPublicSlotApiV1SchedulingPublicLinkIdBookPost({
        path: { link_id: linkId },
        body,
        baseURL: API_BASE_URL,
        throwOnError: false,
      })

      if (bookError || !data) {
        throw new Error(errorDetail(bookError) ?? 'Failed to book slot')
      }

      setConfirmation(data)
      trackEvent(SchedulingEvents.BOOKING_CONFIRMED, {
        link_id: linkId,
        slot_id: selectedSlot.id,
        duration_minutes: link?.duration_minutes,
        additional_attendee_count: additionalAttendees.length,
        ...visitorSlotProps(selectedSlot.id),
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to book slot'
      setError(errorMessage)
      trackEvent(SchedulingEvents.BOOKING_FAILED, {
        link_id: linkId,
        slot_id: selectedSlot.id,
        error_message: errorMessage,
      })
    } finally {
      setBooking(false)
    }
  }

  const togglePollSlot = (slotId: number) => {
    setPollSelectedSlotIds((prev) => {
      const next = new Set(prev)
      if (next.has(slotId)) {
        next.delete(slotId)
      } else {
        next.add(slotId)
      }
      return next
    })
  }

  const handleSubmitPollVotes = async () => {
    if (!linkId || !name || !email) return
    if (pollSelectedSlotIds.size === 0) return

    setPollSubmitting(true)
    try {
      const voteBody: CastPollVotesRequest = {
        voter_email: email,
        voter_name: name,
        slot_ids: Array.from(pollSelectedSlotIds),
      }
      if (token) {
        voteBody.invitee_token = token
      }
      const { error: votesError } = await castPublicPollVotesApiV1SchedulingPublicLinkIdVotesPost({
        path: { link_id: linkId },
        body: voteBody,
        baseURL: API_BASE_URL,
        throwOnError: false,
      })
      if (votesError) {
        throw new Error(errorDetail(votesError) ?? 'Failed to submit votes')
      }
      setPollSubmitted(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit votes'
      setError(errorMessage)
    } finally {
      setPollSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Unavailable</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    )
  }

  if (confirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-12 h-12 text-primary-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Booking confirmed</h1>
          <p className="text-gray-600 mb-4">{confirmation.event_summary}</p>
          <p className="text-gray-500">
            {formatTime(confirmation.start_time, displayTimezone)} &ndash;{' '}
            {formatTime(confirmation.end_time, displayTimezone)}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            A calendar invite has been sent to your email.
          </p>
        </div>
      </div>
    )
  }

  if (pollSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-12 h-12 text-primary-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Votes recorded</h1>
          <p className="text-gray-600">
            Thanks — we&apos;ll let you know which time wins.
          </p>
        </div>
      </div>
    )
  }

  if (!link || !linkId) return <></>

  const sameTimezone = guestTimezone === link.timezone
  // When the page is reached via the /i/:token path on an INVITEE_BOUND link,
  // we got back a resolved invitee — pin the identity fields to that row so
  // the booker can't relabel themselves before submitting.
  const identityLocked = link.invitee != null
  const identityHint = identityLocked
    ? `You were invited as ${link.invitee?.name} <${link.invitee?.email}>.`
    : null
  const emailSuggestion = emailHint?.suggestion ?? null
  const emailLooksInvalid = emailHint != null && !emailHint.suggestion && emailHint.invalid

  const timezoneSelector = (
    <TimezoneSelector
      guestTimezone={guestTimezone}
      linkTimezone={link.timezone}
      ownerName={link.owner_name ?? null}
      sameTimezone={sameTimezone}
      displayTimezone={displayTimezone}
      onChange={setDisplayTimezone}
    />
  )

  const selectedDaySlots = selectedDateKey ? (slotsByDateKey[selectedDateKey] ?? []) : []

  // Rendered in both the slot grid and the empty-grid branch (the latter is
  // the hide-busy escape hatch), so the props live in one place.
  const visitorAvailabilityControls = link.visitor_availability ? (
    <VisitorAvailabilityControls
      config={link.visitor_availability}
      state={visitorAvailability}
      hideBusy={hideBusySlots}
      onConnect={connectVisitorCalendar}
      onDisconnect={disconnectVisitorCalendar}
      onToggleHideBusy={setHideBusySlots}
    />
  ) : null

  const identityFields = (
    <div className="space-y-4">
      {identityHint && (
        <p className="text-sm text-gray-500">{identityHint}</p>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={identityLocked}
          className={INPUT_CLASS + (identityLocked ? ' bg-gray-50 text-gray-500' : '')}
          placeholder="Your name"
        />
      </div>
    </div>
  )

  let body: JSX.Element

  if (link.link_type === 'poll') {
    body = (
      <BookingShell link={link}>
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-1">
            Pick every time that works for you
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {link.owner_name
              ? `${link.owner_name} will pick the winning time once everyone has voted.`
              : 'The host will pick the winning time once everyone has voted.'}
          </p>
          <div className="space-y-4 mb-6">
            {identityFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={identityLocked}
                className={INPUT_CLASS + (identityLocked ? ' bg-gray-50 text-gray-500' : '')}
                placeholder="your@email.com"
              />
            </div>
          </div>
          <div className="mb-4">{timezoneSelector}</div>
          {availableSlots === null ? (
            <div className="space-y-3 animate-pulse" aria-hidden="true">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-9 bg-gray-100 border border-gray-200 rounded-md" />
              ))}
            </div>
          ) : sortedDateKeys.length === 0 ? (
            <p className="text-gray-500">No times offered.</p>
          ) : (
            <div className="space-y-6">
              {sortedDateKeys.map((dateKey) => (
                <div key={dateKey}>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    {formatDayLabelFromKey(dateKey)}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {(slotsByDateKey[dateKey] ?? []).map((slot) => {
                      const checked = pollSelectedSlotIds.has(slot.id)
                      const voteLabel = slot.vote_count
                        ? ` · ${slot.vote_count} vote${slot.vote_count === 1 ? '' : 's'}`
                        : ''
                      return (
                        <label
                          key={slot.id}
                          className={[
                            'flex items-center gap-2 px-3 py-2 text-sm border rounded-md cursor-pointer transition-colors',
                            checked
                              ? 'border-primary-500 text-primary-600 bg-primary-50'
                              : 'border-gray-300 hover:border-primary-500',
                          ].join(' ')}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePollSlot(slot.id)}
                            className="accent-primary-500"
                          />
                          <span>
                            {formatTime(slot.start_time, displayTimezone)}
                            {voteLabel && (
                              <span className="text-gray-400">{voteLabel}</span>
                            )}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-6">
            <button
              onClick={handleSubmitPollVotes}
              disabled={
                !name
                  || !email
                  || pollSelectedSlotIds.size === 0
                  || pollSubmitting
              }
              className="px-4 py-2 text-sm text-white bg-primary-500 rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pollSubmitting
                ? 'Submitting...'
                : `Submit ${pollSelectedSlotIds.size || 'no'} vote${pollSelectedSlotIds.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </BookingShell>
    )
  } else if (selectedSlot) {
    const selectedSummary = (
      <div className="mt-1 rounded-lg bg-gray-50 border border-gray-200 p-3">
        <p className="text-sm font-medium text-gray-900">
          {formatDayLabelFromKey(toDateKey(selectedSlot.start_time, displayTimezone))}
        </p>
        <p className="text-sm text-gray-500 mt-0.5">
          {formatTime(selectedSlot.start_time, displayTimezone)} &ndash;{' '}
          {formatTime(selectedSlot.end_time, displayTimezone)}
        </p>
      </div>
    )
    body = (
      <BookingShell link={link} railChildren={selectedSummary}>
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Enter details</h2>

          <div className="space-y-4">
            {identityFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setEmailHint(null)
                }}
                onBlur={handleVerifyEmail}
                disabled={identityLocked}
                className={INPUT_CLASS + (identityLocked ? ' bg-gray-50 text-gray-500' : '')}
                placeholder="your@email.com"
              />
              {emailChecking && (
                <p className="mt-1 text-xs text-gray-400">Checking…</p>
              )}
              {!emailChecking && emailSuggestion && (
                <p className="mt-1 text-xs text-amber-600">
                  Did you mean{' '}
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => {
                      setEmail(emailSuggestion)
                      setEmailHint(null)
                    }}
                  >
                    {emailSuggestion}
                  </button>
                  ?
                </p>
              )}
              {!emailChecking && emailLooksInvalid && (
                <p className="mt-1 text-xs text-amber-600">
                  Double-check this email — it looks a little off.
                </p>
              )}
            </div>

            {/* Dynamic guest fields */}
            {(link.guest_fields ?? []).map((field) => (
              <GuestFieldInput
                key={field.name}
                field={field}
                value={customFields[field.name] || ''}
                onChange={(value) => setCustomFields({
                  ...customFields,
                  [field.name]: value
                })}
              />
            ))}

            {/* Additional attendees */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional attendees (optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newAttendee}
                  onChange={(e) => setNewAttendee(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddAttendee()
                    }
                  }}
                  className={INPUT_CLASS}
                  placeholder="colleague@email.com"
                />
                <button
                  type="button"
                  onClick={handleAddAttendee}
                  disabled={!newAttendee.trim()}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
              {newAttendee.trim() && (
                <p className="mt-1 text-xs text-amber-600">
                  Add this email or clear the field to continue
                </p>
              )}
              {additionalAttendees.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {additionalAttendees.map((attendee) => (
                    <span
                      key={attendee}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
                    >
                      {attendee}
                      <button
                        type="button"
                        onClick={() => handleRemoveAttendee(attendee)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={INPUT_CLASS}
                rows={3}
                placeholder="Anything you'd like to share ahead of the meeting"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setSelectedSlot(null)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleBook}
              disabled={!isFormValid() || booking}
              className="px-4 py-2 text-sm text-white bg-primary-500 rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {booking ? 'Booking...' : 'Confirm booking'}
            </button>
          </div>
        </div>
      </BookingShell>
    )
  } else {
    body = (
      <BookingShell link={link}>
        {availableSlots === null ? (
          <SlotsAreaSkeleton />
        ) : sortedDateKeys.length === 0 ? (
          // The overlay controls must render here too: with "hide times I'm
          // busy" on, a visitor whose calendar clashes with every slot lands
          // in this branch, and the checkbox is their only way back.
          <div className="p-6">
            <h2 className="text-base font-medium text-gray-900 mb-2">Select a date &amp; time</h2>
            {visitorAvailabilityControls && (
              <div className="max-w-xs">{visitorAvailabilityControls}</div>
            )}
            <p className="text-gray-500">
              {hideBusySlots && (availableSlots?.length ?? 0) > 0
                ? "Every offered time clashes with your calendar. Untick the filter to see them all."
                : 'No available times.'}
            </p>
          </div>
        ) : (
          <div className="md:flex md:divide-x md:divide-gray-200">
            <div className="flex-1 min-w-0 p-6">
              <h2 className="text-base font-medium text-gray-900 mb-4">Select a date &amp; time</h2>
              <MonthCalendar
                availableDateKeys={availableDateKeys}
                selectedDateKey={selectedDateKey}
                onSelectDay={setSelectedDateKey}
                monthCursor={monthCursor}
                onMonthChange={setMonthCursor}
                minMonth={minMonth}
                maxMonth={maxMonth}
              />
            </div>
            <div className="md:w-64 md:shrink-0 p-6 border-t border-gray-200 md:border-t-0">
              {visitorAvailabilityControls}
              {selectedDateKey && (
                <DayTimeList
                  dayLabel={formatDayLabelFromKey(selectedDateKey)}
                  slots={selectedDaySlots}
                  displayTimezone={displayTimezone}
                  timezoneSelector={timezoneSelector}
                  availability={slotAvailabilityMap}
                  onSelect={(slot) => {
                    trackEvent(SchedulingEvents.SLOT_SELECTED, {
                      link_id: linkId,
                      slot_id: slot.id,
                      start_time: slot.start_time,
                      ...visitorSlotProps(slot.id),
                    })
                    setSelectedSlot(slot)
                  }}
                />
              )}
            </div>
          </div>
        )}
      </BookingShell>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {body}

        {!selectedSlot && link.link_type !== 'poll' && (
          <div className="mt-6">
            <ContactAssistantSection link={link} linkId={linkId} />
          </div>
        )}

        <div className="text-center text-xs text-gray-400 mt-8 pb-4">
          Powered by{' '}
          <a
            href={buildCatchHomeHref()}
            onClick={() => trackEvent(SchedulingEvents.POWERED_BY_CATCH_CLICKED, {
              link_id: linkId,
            })}
            className="text-primary-500 hover:underline"
          >
            Catch
          </a>
        </div>
      </div>
    </div>
  )
}
